import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { format, parseISO } from "date-fns";
import { Tooltip } from "@mui/material";
import { Switch, Typography } from "@avenue-8/sidekick-ui";
import RadarIcon from "@mui/icons-material/Radar";

const MONITORING_SINCE_FORMAT = "MMMM d, yyyy, h:mm a";

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
  flex-shrink: 0;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
`;

type RadarMonitoringHeaderToggleProps = {
  isMonitored: boolean;
  isMonitoredSince: string | null;
  isTogglePending?: boolean;
  onToggle: (nextValue: boolean) => void;
  title?: string;
  description?: ReactNode;
};

export const RadarMonitoringHeaderToggle = ({
  isMonitored,
  isMonitoredSince,
  isTogglePending = false,
  onToggle,
  title = "Radar monitoring",
  description = "Life & career updates",
}: RadarMonitoringHeaderToggleProps) => {
  const control = (
    <Container>
      <IconWrap>
        <RadarIcon fontSize="small" />
      </IconWrap>
      <TextColumn>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2">{description}</Typography>
      </TextColumn>
      <Switch
        checked={isMonitored}
        disabled={isTogglePending}
        onChange={(event) => onToggle(event.target.checked)}
        aria-label={title}
      />
    </Container>
  );

  if (isMonitoredSince) {
    return (
      <Tooltip
        title={`Monitoring since ${format(parseISO(isMonitoredSince), MONITORING_SINCE_FORMAT)}`}
      >
        <div>{control}</div>
      </Tooltip>
    );
  }

  return control;
};
